/** @jsx jsx */
import React, {
  useEffect,
  useState,
  createRef,
  Fragment,
  useContext,
  useRef,
  useLayoutEffect,
} from 'react'
import { jsx, Flex, Box, Text } from 'theme-ui'
import Heading from '../shared/Typography/Heading/Heading'
import Body from '../shared/Typography/Body/Body'
import Tag from '../shared/Tag/Tag'
import styled from '@emotion/styled'
import ColorInfoBlock from './ColorInfoBlock'
import ProductDropDown from './ProductDropdown'
import Accordion, { AccordionPanel } from '../shared/Accordion/Accordion'
import { BLOCKS } from '@contentful/rich-text-types'
import SizeGuide from './SizeGuide'
import { CountryContext } from '../../gatsby/wrap-wroot-element'
import { getSubTitle, ProductPrice, ProductSubTitle } from './ProductCard'
import { useWindowSize } from '../../hooks/useWindowSize'

import AddToCart from './AddToCartButton'
import ContentfulRichText from '../shared/Contentful/RichText'
import { currencyDef } from '../../utils/formatPrice'

import { useModal } from 'react-modal-hook'
import Modal from '../shared/Modal/Modal'
import theme from '../../gatsby-plugin-theme-ui'

import { sendEvent } from '../../hooks/useMetrics'
import { getActualShopifyId } from '../../hooks/useShopifyFunctions'

import { mobile as mobileHelper } from '../shared'
import { css } from '@emotion/core'
import { fetchCurrency } from '../../utils/reachLocalize'
import useDocumentPosition from '../../hooks/useDocumentPosition'
import { LoopContext, ShopifyContext } from '../Layout'
import useReachCurrency from '../../hooks/useReachCurrency'
import useDimensions from '../../hooks/useDimensions'
import fairPricing from '../../icons/fairPricing.png'
import ProductHeading from './ProductHeading'
import { navigate } from 'gatsby'
import AfterPay from './AfterPay'
import CustomStarRating from '../shared/StarRating/StarRating'
import { GorgiasButton } from '../../components/footer/ColumnLink'

const TagWrap = styled.div``

const options = {
  [BLOCKS.PARAGRAPH]: (node, children) => (
    <AccordianText>{children}</AccordianText>
  ),
}

const AccordianText = ({ children, details = false }) => {
  return (
    <Body
      variant={details ? 'body2semibold' : 'body2'}
      sx={{ fontSize: [0, 0, 0, 0, 0, 0] }}
    >
      {children}
    </Body>
  )
}
const ProductPageHeader = props => {
  const { setCheckoutButtonisFixed } = useContext(LoopContext)

  const {
    getRegionalPrice,
    getRegionalCompareAt,
    loaded,
    convertedCurrency,
    notUs,
  } = useReachCurrency(props.variants)

  const { mobile, smallScreen } = useWindowSize()

  const [variantOptions, setVariantOptions] = useState({
    materials: [],
    thicknesses: [],
  })
  const [defaultVariant, setDefaultVariant] = useState(false)

  const [selectedVariantId, setSelectedVariantId] = useState('')

  // AddtoCart Button Ref
  const buttonRef = createRef()

  const { fixedPosition } = mobile
    ? useDocumentPosition(buttonRef, undefined, setCheckoutButtonisFixed)
    : {}

  const isSolidGold = props.tags.includes('material_solid-gold')

  useEffect(() => {
    setVariantOptions({
      materials: props.materials,
      thicknesses: props.thicknesses,
    })

    function checkUnpopularSizes(node = {}) {
      return !node.title.includes('26') && !node.title.endsWith('Extra Large')
    }

    const liveVariants =
      props.liveProduct &&
      props.liveProduct.variants &&
      props.liveProduct.variants.edges

    const highestInv =
      liveVariants &&
      liveVariants
        .filter(i => checkUnpopularSizes(i.node))
        .reduce(
          (prev, current) =>
            prev.quantityAvailable > current.node.quantityAvailable
              ? prev
              : current.node,
          {}
        )
    const isGiftCard = props.productType === 'GIFT CARD'
    const giftCardDefault = isGiftCard
      ? props.variants.find(p => p.price === '100.00').shopifyId
      : false

    const defaultTagVariant = props.tags.find(t => t.startsWith('default_'))

    const defaultVarByTag =
      defaultTagVariant && liveVariants
        ? liveVariants.find(p => {
            return (
              p.node.sku === defaultTagVariant.split('_')[1] &&
              p.node.availableForSale
            )
          })
        : {}

    const defaultVarID =
      defaultVarByTag && defaultVarByTag.node && defaultVarByTag.node.id
    const highestInvAvailable =
      highestInv && highestInv.availableForSale && highestInv.id

    const lastAvailable =
      liveVariants && liveVariants.find(i => i.node.availableForSale)

    const firstVariant = props.variants[0] && props.variants[0].shopifyId

    const defaultVariantId =
      (isSolidGold && firstVariant) ||
      defaultVarID ||
      giftCardDefault ||
      highestInvAvailable ||
      (lastAvailable && lastAvailable.node.id) ||
      firstVariant

    !props.loading &&
      props.liveProduct.id &&
      setDefaultVariant(
        props.variants.find(i => i.shopifyId === defaultVariantId)
      )
    setSelectedVariantId(defaultVariantId)
  }, [props.liveProduct])

  useEffect(() => {
    if (defaultVariant) {
      const selVariant = props.variants.find(
        i => i.shopifyId === selectedVariantId
      )

      const dataLayerOpts = {
        event: 'dl_view_item',
        ecommerce: {
          currencyCode: currencyDef,
          detail: {
            actionField: { list: location.pathname },
            products: [
              {
                name: props.title + ' ' + selVariant.title, // Name or ID is required.
                id: selVariant.sku,
                product_id: getActualShopifyId(props.shopifyId, 'Product'),
                variant_id: getActualShopifyId(
                  selVariant.shopifyId,
                  'ProductVariant'
                ),
                price: parseFloat(selVariant.price),
                brand: 'JAXXON',
                variant: selVariant.title,
                category: props.productType,
                inventory: '1',
              },
            ],
          },
        },
      }

      sendEvent(dataLayerOpts)
    }
  }, [defaultVariant, selectedVariantId])

  const currentLiveProductVariant =
    props.liveProduct &&
    props.liveProduct.variants &&
    props.liveProduct.variants.edges.find(i => i.node.id === selectedVariantId)

  const currentLiveProductNode = currentLiveProductVariant
    ? currentLiveProductVariant.node
    : {}

  const isTryNowProduct = props.tags.includes('trynow') && !isSolidGold

  const isFixed = {
    position: 'fixed',
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: theme.zIndex[5],
    width: '100%',
    borderRadius: 0,
  }

  const currentValue = {
    ...props.variants.find(i => i.shopifyId === selectedVariantId),
    index: props.variants.findIndex(i => i.shopifyId === selectedVariantId),
    ...currentLiveProductNode,
  }

  const regionalCompareAt = getRegionalCompareAt(currentValue.index)
  const regionalPrice = getRegionalPrice(currentValue.index)

  const currentPrice = regionalPrice || currentValue.price
  const currentCompareAtPrice = regionalCompareAt || currentValue.compareAtPrice

  const isGiftCard = props.productType === 'GIFT CARD'

  useEffect(() => {
    props.setProductInfo({
      currentValue: currentValue,
      currentPrice: currentPrice,
      loaded: loaded,
      currentCompareAtPrice: currentCompareAtPrice,
      convertedCurrency: convertedCurrency,
    })
  }, [loaded])

  const generateProductLink = (url, newVariant) => {
    return url.replace(
      /2mm|2-5mm|3mm|4mm|5mm|6mm|7mm|8mm|9mm|10mm|$/,
      newVariant
    )
  }

  const getHelperText = size => {
    switch (size) {
      case '10':
        return '(Most Popular)'
      case '18':
        return ''
      case '20':
        return '(Short)'
      case '22':
        return '(Most Popular)'
      case '24':
        return '(Classic)'
      case '26':
        return '(Long)'
      case '7"':
        return '(Small)'
      case '7.5"':
        return '(Most Popular)'
      case '8"':
        return '(Large)'
      case '8.5"':
        return '(Extra Large)'
      default:
        return ''
    }
  }

  const convertToHyperlink = text => {
    let description = text
    const hyperlinkRE = /\[[a-zA-Z0-9]+\]\([a-zA-Z0-9\\\\\:\.]+\)/g
    const hyperlinks = description.match(hyperlinkRE)
    hyperlinks &&
      hyperlinks.forEach(i => {
        const word = i.match(/\[.+\]/g)
        const link = i.match(/\(.+\)/g)
        const parsedWord = word[0].slice(1, word[0].length - 1)
        const parsedLink = link[0].slice(1, link[0].length - 1)
        description = description.replace(
          i,
          `<a href="${parsedLink}">${parsedWord}</a>`
        )
      })
    return (description =
      '<Body variant="body2">' + description.slice(0, -1) + '</Body>')
  }
  const createMarkUp = () => {
    return {
      __html: convertToHyperlink(props.details.description),
    }
  }

  const noneMetailOptions = props.options.filter(
    i => !['Material:', 'Metal:'].includes(i.name)
  )

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        alignItems: 'stretch',
        position: ['unset', null, null, 'sticky'],
        top: '8rem',
      }}
    >
      {!smallScreen && (
        <ProductHeading
          loaded={loaded}
          currentValue={currentValue}
          currentPrice={currentPrice}
          currentCompareAtPrice={currentCompareAtPrice}
          convertedCurrency={convertedCurrency}
          title={props.title}
          avgScore={props.avgScore}
          executeScroll={props.executeScroll}
          numberOfBusinessReviews={props.numberOfBusinessReviews}
          primaryTag={props.primaryTag}
          secondaryTag={props.secondaryTag}
        />
      )}
      <Flex
        sx={{
          alignItems: 'center',
          mt: '5px',
          mb: '10px',
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        {props.materials.length > 0 && (
          <ColorInfoBlock materials={props.materials} {...props} />
        )}

        {props.avgScore ? (
          <Flex
            sx={{ cursor: 'pointer', justifyContent: 'flex-end' }}
            onClick={props.executeScroll}
          >
            <CustomStarRating
              name="productCardRating"
              rating={props.avgScore}
              reviewCount={props.numberOfBusinessReviews}
              starDimension="15px"
            />
          </Flex>
        ) : (
          <Fragment />
        )}
      </Flex>

      {noneMetailOptions &&
        noneMetailOptions.map((i, k) => {
          const sO =
            currentValue.selectedOptions &&
            currentValue.selectedOptions.find(so => so.name === i.name)
          const isMostPopular =
            defaultVariant &&
            defaultVariant.selectedOptions.find(dv => dv.name === i.name)

          const defVal = sO &&
            sO['value'] && {
              value: sO['value'],
              label: `${sO['value']} ${getHelperText(sO['value'])}`,
            }

          return i.values.length > 1 ? (
            <ProductDropDown
              key={k}
              name={i.name}
              defaultValue={defVal}
              value={defVal}
              // sx={{
              //   mb: '10px',
              // }}
              customValueLabel={
                defVal &&
                props.variants.length > 1 &&
                (defVal.label && defVal.label.trim()) === isMostPopular.value &&
                '(Most Popular)'
              }
              selectionTitle={i.name}
              sizeGuides={props.sizeGuides}
              options={i.values.map(o => ({
                label: `${o} ${getHelperText(o)}`,
                value: o,
              }))}
              onChange={selected => {
                const sel = props.variants.find(i =>
                  i.title.includes(selected.value)
                )

                setSelectedVariantId(sel ? sel.shopifyId : selectedVariantId)
              }}
            />
          ) : (
            <Flex
              sx={{
                flexDirection: 'row',
                mb: '10px',
                ml: '3px',
              }}
            >
              <Body variant="caption" sx={{ fontWeight: 'bold' }}>
                {i.name}
              </Body>
              <Body variant="caption" sx={{ ml: '3px' }}>
                {i.values}
              </Body>
            </Flex>
          )
        })}

      {variantOptions.thicknesses && variantOptions.thicknesses.length > 0 && (
        <ProductDropDown
          selectionTitle="Thickness:"
          options={variantOptions.thicknesses
            .map(t => ({
              label: t.replace('-', '.'),
              value: t,
            }))
            .sort((a, b) => {
              return parseInt(a.value) - parseInt(b.value)
            })}
          onChange={selected => {
            navigate(
              `/products/${generateProductLink(
                props.handle,
                selected.value.split(' ')[0]
              )}`
            )
          }}
          value={{
            value: variantOptions.thicknesses[0],
            label: variantOptions.thicknesses[0].replace('-', '.'),
          }}
          sx={
            {
              // mb: '30px',
            }
          }
          // sizeGuides={props.sizeGuides}
        />
      )}
      {props.sizeGuides &&
        props.sizeGuides.length > 0 &&
        noneMetailOptions &&
        noneMetailOptions.find(i => i.values.length >= 1) && (
          <SizeGuideModal {...props} />
        )}
      <AddToCart
        size="large"
        title={
          <Flex
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '35px',
            }}
          >
            <Body>Add To Cart</Body>
            {loaded && (
              <ProductPrice
                price={currentValue && currentPrice}
                compareAtPrice={currentValue && currentCompareAtPrice}
                currency={convertedCurrency}
                textColor="white"
              />
            )}
          </Flex>
        }
        variant="secondary"
        shopifyId={selectedVariantId}
        availableForSale={currentValue.availableForSale}
        convertedPrice={currentValue && currentPrice}
        convertedCompareAtPrice={currentValue && currentCompareAtPrice}
        convertedCurrency={currentValue && convertedCurrency}
        sx={fixedPosition && mobile ? isFixed : { mb: '10px', mt: '5px' }}
        productType={props.productType}
      />
      <Box ref={buttonRef} />
      {currentValue.availableForSale && isTryNowProduct && (
        <AddToCart
          size="large"
          variant="white"
          title="TRY BEFORE YOU BUY"
          shopifyId={selectedVariantId}
          sx={{
            mb: '5px',
          }}
          availableForSale={currentValue.availableForSale}
          tryNow
          productType={props.productType}
        />
      )}
      {!notUs && <AfterPay currentValue={currentValue} mobile={mobile} />}
      {/* {isTryNowProduct && (
        <TryNowTextWrap
          sx={{
            mb: '25px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Flex sx={{ alignItems: 'center' }}>
            <img
              src={Checkicon}
              sx={{
                width: '20px',
                display: 'inline',
                mr: '5px',
              }}
            />
            <Body
              variant="body2semibold"
              sx={{
                alignSelf: 'flex-start',
                display: 'inline',
              }}
            >
              Try at home for 5 days FREE
            </Body>
          </Flex>
          <Body
            variant="textLinkSmall"
            sx={{
              textDecoration: 'underline',
            }}
            onClick={showModal}
          >
            How it Works
          </Body>
        </TryNowTextWrap>
      )} */}
      <Box>
        {props.companyValueTextSection.length > 0 && (
          <Flex
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              borderTop: '2px solid #EAE7E0',
              borderBottom: '2px solid #EAE7E0',
              mt: '14px',
            }}
          >
            {props.companyValueTextSection.map((item, k) => {
              return (
                <Flex
                  key={k}
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    m: '20px 0px',
                  }}
                >
                  <img
                    src={item.icon.file.url}
                    sx={{ width: '42px', margin: 'auto', mb: 20 }}
                  ></img>
                  <Text
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      width: mobile ? '90%' : '100%',
                    }}
                  >
                    {item.value}
                  </Text>
                </Flex>
              )
            })}
          </Flex>
        )}
      </Box>

      {!isGiftCard && !notUs && <PriceCompare price={currentPrice} />}
      <Box sx={{ py: 0, px: 0 }}>
        {props.details.description && (
          <AccordionPanel open>
            <Body
              variant="body2"
              dangerouslySetInnerHTML={createMarkUp()}
            ></Body>
          </AccordionPanel>
        )}
      </Box>
      {currentValue.details && Object.keys(currentValue.details).length > 0 && (
        <Accordion
          title={'Details'}
          links={[]}
          v="productAccordion"
          renderItem={
            <AccordianText details>
              {currentValue.details &&
                Object.entries(currentValue.details).map(([key, value], i) => {
                  return value ? (
                    <Flex sx={{ justifyContent: 'space-between', fontSize: 0 }}>
                      <Box>
                        {key
                          .split('details')[1]
                          .replace(/([A-Z])/g, ' $1')
                          .trim()}
                      </Box>
                      <Box>{value}</Box>
                    </Flex>
                  ) : (
                    <Fragment />
                  )
                })}
            </AccordianText>
          }
        />
      )}
      {props.details && props.details['accordianMaterial'] && (
        <Accordion
          title={'Material'}
          links={[]}
          v="productAccordion"
          renderItem={
            <AccordianText>{props.details['accordianMaterial']}</AccordianText>
          }
        />
      )}
      {props.details && props.details['accordianCareTips'] && (
        <Accordion
          title={'Care Tips'}
          links={[]}
          v="productAccordion"
          renderItem={
            <AccordianText>{props.details['accordianCareTips']}</AccordianText>
          }
        />
      )}
      {/* {props.accordion.accordionSteps.map((item, index) => (
        <Accordion
          key={index}
          title={item.value}
          links={[]}
          v="productAccordion"
          renderItem={
            <ContentfulRichText
              text={item.description}
              customOptions={options}
            />
          }
        />
      ))} */}

      <Flex
        sx={{
          flexDirection: 'row',
          mt: '15px',
        }}
      >
        <Body variant="captionBold">Need product help?</Body>
        <GorgiasButton
          render={({ setShowGorgias }) => (
            <Body
              variant="caption"
              onClick={() => setShowGorgias()}
              sx={{
                ml: '3px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Chat
            </Body>
          )}
        />
        <Body variant="caption" sx={{ ml: '3px' }}>
          with an Expert
        </Body>
      </Flex>
    </Flex>
  )
}

const StyledBox = styled(Box)`
  max-height: 100%;
  ${mobileHelper(css`
    max-width: 100%;
  `)}
`

const PriceCompare = props => {
  const { mobile } = useWindowSize()

  const targetRef = useRef()
  const size = useDimensions(targetRef)
  const [showModal, hideModal] = useModal(
    () => (
      <Modal flush isOpen onRequestClose={hideModal} hideModal={hideModal}>
        <Box
          sx={{
            margin: '30px 20px',
          }}
        >
          <Body
            variant="body2bold"
            sx={{
              textAlign: 'center',
              mb: '10px',
              fontWeight: '800',
            }}
          >
            Fair Pricing
          </Body>
          <Body
            variant="caption"
            sx={{
              textAlign: 'center',
            }}
          >
            Major brands in our industry markup their products 8-10x the actual
            cost. We do things differently. To bring luxury within reach, we've
            built relationships with some of the world’s top jewelers from Los
            Angeles to Italy to sell directly to you.
          </Body>
          <img
            src={fairPricing}
            alt="Jaxxon vs retail pricing"
            sx={{ width: '100%' }}
          />
        </Box>
      </Modal>
    ),
    []
  )

  return (
    <Box
      sx={{
        bg: 'lightGray',
        mt: '20px',
        padding: '10px 0px 40px 0px',

        // justifyContent: 'center',
      }}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          px: '20px',
        }}
      >
        <Body
          variant="caption"
          sx={{
            letterSpacing: '1px',
            fontWeight: 500,
          }}
        >
          FAIR PRICING
        </Body>

        <Body
          variant="caption"
          sx={{
            textDecoration: 'underline',
            textAlign: 'right',
            cursor: 'pointer',
            letterSpacing: '1px',
          }}
          onClick={showModal}
        >
          Learn more
        </Body>
      </Flex>

      <Flex
        ref={targetRef}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: size.width < 370 && !mobile ? 'column' : 'row',
          pt: '30px',
          // mr: size.width < 370 ? 0 : '28px',
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent:
              size.width < 370 || mobile ? 'center' : 'flex-start',
            flex: size.width < 370 || mobile ? 4 : 2,
            flexDirection: size.width < 370 || mobile ? 'column' : 'row',
            ml: size.width < 370 || mobile ? 0 : '1.2em',
          }}
        >
          <Body
            variant="caption"
            sx={{
              fontSize: '14px',

              mr: size.width < 370 || mobile ? 0 : '7px',
              mb: size.width < 370 || mobile ? '5px' : 0,
            }}
          >
            {props.price && `$${Math.round(props.price)}`}
          </Body>
          <Body
            variant="captionBold"
            sx={{
              letterSpacing: '2px',
              fontWeight: '600',
              fontSize: '10px',
            }}
          >
            JAXXON
          </Body>
        </Flex>

        {size.width < 370 && !mobile ? (
          <Body
            sx={{
              textAlign: 'center',
              mb: '20px',
            }}
          >
            _____________________
          </Body>
        ) : (
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <Body sx={{ borderLeft: '1px solid black', height: '30px' }}></Body>
          </Flex>
        )}

        <Flex
          sx={{
            flex: size.width < 370 || mobile ? 4 : 5,
            justifyContent:
              size.width < 370 || mobile ? 'center' : 'flex-start',
            flexDirection: size.width < 370 || mobile ? 'column' : 'row',
            alignItems: 'center',
            // ml: size.width < 370 || mobile ? 0 : '1em',
          }}
        >
          <Body
            variant="caption"
            sx={{
              fontSize: '14px',
              mr: size.width < 370 || mobile ? 0 : '7px',
              mb: size.width < 370 || mobile ? '5px' : 0,
            }}
          >
            {props.price && `$${Math.round((props.price * 2.6) / 100) * 100}`}
          </Body>
          <Body
            variant="captionBold"
            sx={{
              letterSpacing: '2px',
              fontWeight: '600',
              fontSize: '10px',
            }}
          >
            ESTIMATED RETAIL
          </Body>
        </Flex>
      </Flex>
    </Box>
  )
}

const SizeGuideModal = props => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal
        desktopStyles={{
          content: {
            margin: 'auto',
            maxWidth: 'fit-content',
            maxHeight: 'fit-content',
          },
        }}
        smallScreenStyles={{
          content: {
            height: 'auto',
            width: '100vw',
            left: '0px',
            padding: 0,
            paddingBottom: 15,
            paddingTop: 15,
            maxHeight: 'fit-content',
          },
        }}
        flush
        isOpen
        onRequestClose={hideModal}
        hideModal={hideModal}
      >
        <StyledBox sx={{ margin: 'auto' }}>
          <SizeGuide
            sizeGuides={
              props.sizeGuides
                ? props.sizeGuides.sort(function(a, b) {
                    return a.shopifyTag.includes('chain')
                      ? -1
                      : b.shopifyTag.includes('chain')
                      ? 1
                      : 0
                    return
                  })
                : []
            }
          />
        </StyledBox>
      </Modal>
    ),
    []
  )
  return (
    <Body
      sx={{
        textDecoration: 'underline',
        mt: '5px',
        mb: '15px',
        fontWeight: 'semiBold',
        cursor: 'pointer',
        ml: '5px',
      }}
      variant="caption"
      onClick={showModal}
    >
      See Size Chart
    </Body>
  )
}

export default ProductPageHeader
